import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import { NWClient } from '../../client/NWClient';
import { Supplier, SupplierSearchData } from '../../models/supplier.model';
import { RootState, revertAll } from '../../store';
import { SupplierNote } from '../../models/supplier.note.model';
import { SupplierContact } from '../../models/supplier.contact.model';

export const fetchSuppliers = createAsyncThunk(
  'companies/fetchSuppliers',
  async ({ company_id }: SupplierSearchData, { rejectWithValue }) => {
    if (company_id === undefined) {
      return rejectWithValue('Company ID is required');
    }

    const response = await NWClient.list('supplier_profile', { company_id });

    return response;
  }
);

export const updateSupplier = createAsyncThunk(
  'suppliers/updateSupplier',
  async (updatedSupplier: Supplier, { rejectWithValue }) => {
    try {
      const response = await NWClient.put(
        'supplier_profile',
        updatedSupplier.id,
        updatedSupplier,
        true,
        false
      );
      return response as Supplier;
    } catch (error) {
      return rejectWithValue(error.message);
    }
  }
);

export const fetchSupplierNotes = createAsyncThunk(
  'suppliers/fetchSupplierNotes',
  async (supplierId: number, { rejectWithValue }) => {
    if (supplierId === undefined) {
      return rejectWithValue('Supplier ID is required');
    }
    try {
      const response = (await NWClient.get('supplier_profile', supplierId)) as Supplier;
      return { supplierId, notes: response.notes as SupplierNote[] };
    } catch (error) {
      return rejectWithValue('Failed to fetch supplier notes');
    }
  }
);

export const updateSupplierNotes = createAsyncThunk(
  'suppliers/updateSupplierNotes',
  async ({ supplierId, note }: { supplierId: number; note: SupplierNote }, { dispatch }) => {
    try {
      await NWClient.put(`supplier-notes`, note.id, note);
      // After successful update, fetch updated notes to refresh state
      await dispatch(fetchSupplierNotes(supplierId));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const deleteSupplierNote = createAsyncThunk(
  'suppliers/deleteSupplierNote',
  async ({ supplierId, noteId }: { supplierId: number; noteId: number }, { dispatch }) => {
    try {
      await NWClient.delete(`supplier-notes`, noteId);
      // After successful deletion, fetch updated notes to refresh state
      await dispatch(fetchSupplierNotes(supplierId));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const createSupplierContact = createAsyncThunk(
  'suppliers/createSupplierContact',
  async (
    {
      supplierId,
      contact,
      company_id,
    }: { supplierId: number; contact: Partial<SupplierContact>; company_id: number },
    { dispatch }
  ) => {
    try {
      const formData = new FormData();

      Object.entries(contact).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });

      const response = await NWClient.post('supplier_profile_contacts', formData, true);
      await dispatch(fetchSuppliers({ company_id }));
      return { success: true, contact: response };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const updateSupplierContacts = createAsyncThunk(
  'suppliers/updateSupplierContacts',
  async (
    {
      supplierId,
      contact,
      companyId,
    }: { supplierId: number; contact: SupplierContact; companyId: number },
    { dispatch }
  ) => {
    try {
      const formData = new FormData();

      Object.entries(contact).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          formData.append(key, value.toString());
        }
      });
      await NWClient.put('supplier_profile_contacts', contact.id, formData, true, true);
      await dispatch(fetchSuppliers({ company_id: companyId }));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export const deleteSupplierContacts = createAsyncThunk(
  'suppliers/deleteSupplierContacts',
  async ({ companyId, contactId }: { companyId: number; contactId: number }, { dispatch }) => {
    try {
      await NWClient.delete('supplier_profile_contacts', contactId);
      await dispatch(fetchSuppliers({ company_id: companyId }));
      return { success: true };
    } catch (error) {
      return { success: false, error };
    }
  }
);

export interface SuppliersState {
  suppliersList: Supplier[];
  status: string;
  error: string;
  notes: SupplierNote[];
}

const suppliersArray: Supplier[] = [];
const initialState: SuppliersState = {
  suppliersList: suppliersArray,
  status: 'idle',
  error: '',
  notes: [],
};

const suppliersSlice = createSlice({
  name: 'suppliers',
  initialState,
  reducers: {
    clearSupplierNotes(state) {
      state.notes = []; // Reset notes to an empty array
    },
  },
  extraReducers(builder) {
    builder
      .addCase(fetchSuppliers.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSuppliers.fulfilled, (state, action) => {
        if (Array.isArray(action.payload)) {
          state.suppliersList = [...action.payload] as Supplier[];
          state.status = 'succeeded';
        }
      })
      .addCase(fetchSuppliers.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(updateSupplier.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateSupplier.fulfilled, (state, action) => {
        const index = state.suppliersList.findIndex(
          (supplier) => supplier.id === action.payload.id
        );
        if (index !== -1) {
          state.suppliersList[index] = action.payload as Supplier;
        }
        state.status = 'succeeded';
      })
      .addCase(updateSupplier.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message;
      })
      .addCase(fetchSupplierNotes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(fetchSupplierNotes.fulfilled, (state, action) => {
        const { supplierId, notes } = action.payload;
        state.notes = notes;
        state.status = 'succeeded';
      })
      .addCase(fetchSupplierNotes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message || 'Failed to fetch supplier notes';
      })
      .addCase(updateSupplierNotes.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateSupplierNotes.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(updateSupplierNotes.rejected, (state, action) => {
        state.status = 'failed';
        state.error = 'Failed to update supplier note';
      })
      .addCase(deleteSupplierNote.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteSupplierNote.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(deleteSupplierNote.rejected, (state, action) => {
        state.status = 'failed';
        state.error = 'Failed to delete supplier note';
      })
      .addCase(createSupplierContact.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(createSupplierContact.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(createSupplierContact.rejected, (state, action) => {
        state.status = 'failed';
        state.error = 'Failed to create supplier contact';
      })
      .addCase(updateSupplierContacts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(updateSupplierContacts.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(updateSupplierContacts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = 'Failed to update supplier contact';
      })
      .addCase(deleteSupplierContacts.pending, (state) => {
        state.status = 'loading';
      })
      .addCase(deleteSupplierContacts.fulfilled, (state, action) => {
        state.status = 'succeeded';
      })
      .addCase(deleteSupplierContacts.rejected, (state, action) => {
        state.status = 'failed';
        state.error = 'Failed to delete supplier contact';
      })
      .addCase(revertAll, () => initialState);
  },
});

export const { clearSupplierNotes } = suppliersSlice.actions;

export default suppliersSlice.reducer;

export const selectAllSuppliers = (state: RootState) => state.suppliers.suppliersList;
export const selectSupplierById = (state: RootState, supplierId: number) =>
  state.suppliers.suppliersList.find((item) => item.id === supplierId);
export const selectSupplierNotes = (state: RootState) => state.suppliers.notes;
